import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, ExternalLink, Globe2, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useProviderCheckoutDomainSettings,
  useUpdateProviderCheckoutDomainSettings,
} from '@/hooks/provider/useProviderCheckoutDomainSettings';
import {
  normalizeProviderCheckoutDomain,
  ProviderCheckoutDomainRequestError,
} from '@/services/provider/checkout-domain.service';

type FormState = {
  checkoutDomain?: string;
  checkoutDomainEnabled: boolean;
};

const MAX_LENGTH = 255;
const CHECKOUT_PREFIX = 'checkout.';
const AUTH_PREFIX = 'auth.';

const normalizeBaseDomain = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return '';
  }
  const lower = trimmed.toLowerCase();
  if (lower.startsWith(CHECKOUT_PREFIX)) {
    return trimmed.slice(CHECKOUT_PREFIX.length);
  }
  if (lower.startsWith(AUTH_PREFIX)) {
    return trimmed.slice(AUTH_PREFIX.length);
  }
  return trimmed;
};

function isValidHost(value: string): boolean {
  if (value.length === 0) {
    return false;
  }
  if (value.includes('://') || value.includes('/') || value.includes('?') || value.includes('#')) {
    return false;
  }
  if (/\s/.test(value)) {
    return false;
  }

  try {
    const parsed = new URL(`http://${value}`);
    if (parsed.port) {
      return false;
    }
    if (parsed.hostname.toLowerCase() !== value.toLowerCase()) {
      return false;
    }
    if (!parsed.hostname.includes('.')) {
      return false;
    }
    const labels = parsed.hostname.split('.');
    if (labels.some((label) => label.length === 0 || label.length > 63)) {
      return false;
    }
    if (
      labels.some(
        (label) =>
          !/^[a-z0-9-]+$/i.test(label) || label.startsWith('-') || label.endsWith('-'),
      )
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function ProviderCheckoutDomainSettingsCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderCheckoutDomainSettings();
  const updateMutation = useUpdateProviderCheckoutDomainSettings();

  const schema = useMemo(
    () =>
      z
        .object({
          checkoutDomainEnabled: z.boolean(),
          checkoutDomain: z
            .string()
            .trim()
            .max(MAX_LENGTH, {
              message: t(
                'settings.checkoutDomain.validation.tooLong',
                'Checkout domain must be 255 characters or fewer.',
              ),
            })
            .optional(),
        })
        .superRefine((values, ctx) => {
          const normalized = values.checkoutDomain?.trim() ?? '';
          if (values.checkoutDomainEnabled && normalized.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'settings.checkoutDomain.validation.required',
                'Enter a base domain to enable custom domains.',
              ),
              path: ['checkoutDomain'],
            });
          }
          if (normalized.length > 0 && !isValidHost(normalized)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'settings.checkoutDomain.validation.invalid',
                'Enter a valid host (no scheme, no path, no port).',
              ),
              path: ['checkoutDomain'],
            });
          }
        }),
    [t],
  );

  const form = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues: {
      checkoutDomain: '',
      checkoutDomainEnabled: false,
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      form.reset({
        checkoutDomain: normalizeBaseDomain(settingsQuery.data.checkoutDomain),
        checkoutDomainEnabled: settingsQuery.data.checkoutDomainEnabled ?? false,
      });
    }
  }, [form, settingsQuery.data]);

  const isSaving = updateMutation.isPending;
  const checkoutDomainEnabled = form.watch('checkoutDomainEnabled');
  const watchedDomain = normalizeBaseDomain(form.watch('checkoutDomain'));
  const checkoutPreview = watchedDomain ? `${CHECKOUT_PREFIX}${watchedDomain}` : '';
  const authPreview = watchedDomain ? `${AUTH_PREFIX}${watchedDomain}` : '';

  const handleSubmit = async (values: FormState) => {
    const normalized = normalizeProviderCheckoutDomain(
      normalizeBaseDomain(values.checkoutDomain),
    );
    try {
      const updated = await updateMutation.mutateAsync({
        checkoutDomain: normalized,
        checkoutDomainEnabled: values.checkoutDomainEnabled,
      });
      form.reset({
        checkoutDomain: updated.checkoutDomain ?? '',
        checkoutDomainEnabled: updated.checkoutDomainEnabled ?? false,
      });
      toast.success(
        t('settings.checkoutDomain.messages.saveSuccess', 'Checkout domain settings updated.'),
      );
    } catch (error) {
      form.clearErrors('checkoutDomain');
      form.clearErrors('checkoutDomainEnabled');
      if (error instanceof ProviderCheckoutDomainRequestError && error.errors?.checkout_domain?.[0]) {
        form.setError('checkoutDomain', { type: 'server', message: error.errors.checkout_domain[0] });
      }
      if (
        error instanceof ProviderCheckoutDomainRequestError &&
        error.errors?.checkout_domain_enabled?.[0]
      ) {
        form.setError('checkoutDomainEnabled', {
          type: 'server',
          message: error.errors.checkout_domain_enabled[0],
        });
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.checkoutDomain.messages.saveError', 'Unable to update checkout domain settings.');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-muted-foreground" />
            {t('settings.checkoutDomain.title', 'Checkout domain')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutDomain.description',
              'Set a custom domain used only for public checkout flows.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.checkoutDomain.title', 'Checkout domain')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutDomain.description',
              'Set a custom domain used only for public checkout flows.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.checkoutDomain.messages.loadError', 'Unable to load checkout domain settings.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-muted-foreground" />
          {t('settings.checkoutDomain.title', 'Checkout domain')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.checkoutDomain.description',
            'Set a custom domain used only for public checkout flows.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="checkoutDomainEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                  <div className="space-y-1">
                    <FormLabel className="text-base">
                      {t(
                        'settings.checkoutDomain.fields.enabled',
                        'Enable custom domains',
                      )}
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'settings.checkoutDomain.helpers.enabled',
                        'Use the custom checkout and auth subdomains when enabled.',
                      )}
                    </p>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSaving} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="checkoutDomain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.checkoutDomain.fields.domain', 'Checkout domain')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('settings.checkoutDomain.placeholders.domain', 'acme.com')}
                      disabled={isSaving || !checkoutDomainEnabled}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.checkoutDomain.helper',
                      'Use a host only (no https://, no path, no port). Enter the base domain.',
                    )}
                  </p>
                  {checkoutDomainEnabled && checkoutPreview && authPreview ? (
                    <div className="text-xs text-muted-foreground">
                      <p>
                        {t('settings.checkoutDomain.preview.checkout', 'Checkout:')}{' '}
                        <span className="font-mono text-foreground/80">{checkoutPreview}</span>
                        <span>
                          {' '}
                          {t(
                            'settings.checkoutDomain.preview.checkoutSuffix',
                            '(We will use for checkout)',
                          )}
                        </span>
                      </p>
                      <p>
                        {t('settings.checkoutDomain.preview.auth', 'Auth:')}{' '}
                        <span className="font-mono text-foreground/80">{authPreview}</span>
                        <span>
                          {' '}
                          {t('settings.checkoutDomain.preview.authSuffix', '(We will use for login)')}
                        </span>
                      </p>
                      <div className="mt-3 rounded-md border border-foreground/10 bg-foreground/5 px-3 py-2 text-foreground">
                        <p className="text-sm font-medium">
                          {t(
                            'settings.checkoutDomain.helperDerived',
                            'We will use checkout.<base-domain> for checkout and auth.<base-domain> for login.',
                          )}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>{t('settings.checkoutDomain.helpers.dnsLinksLabel', 'DNS guides:')}</span>
                          <a
                            href="https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/"
                            className="inline-flex items-center gap-1 text-primary underline"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('settings.checkoutDomain.helpers.cloudflare', 'Cloudflare')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <a
                            href="https://www.godaddy.com/help/manage-dns-records-680"
                            className="inline-flex items-center gap-1 text-primary underline"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('settings.checkoutDomain.helpers.godaddy', 'GoDaddy')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <a
                            href="https://www.namecheap.com/support/knowledgebase/article.aspx/319/2237/how-can-i-set-up-an-a-address-record-for-my-domain/"
                            className="inline-flex items-center gap-1 text-primary underline"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('settings.checkoutDomain.helpers.namecheap', 'Namecheap')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset({ checkoutDomain: '', checkoutDomainEnabled: false })}
                disabled={isSaving}
              >
                {t('common.clear', 'Clear')}
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
                {t('settings.checkoutDomain.actions.save', 'Save checkout domain')}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
